/* ══════════════════ Jungle Mode — Route Definitions & Helpers ══════════════════ */

import { JungleCamp, CELL_SIZE } from './simulationTypes';
import { CAMPS, lerpByLevel, champLevelFromXP } from '../JungleSimulator/utils';
import type { CampId } from '../JungleSimulator/utils';
import type { RouteDirection, JungleModeConfig } from './jungleModeTypes';

/* ── Route camp ID arrays ──
 * NOTE: In the walkability grid data, camp side prefixes are swapped relative
 * to the actual map layout. "red-" prefixed camps are physically on blue's
 * side of the map (bottom-left), and "blue-" prefixed camps are on red's side
 * (top-right). The routes below use the correct camps for each team.
 */

const BLUE_TOP_TO_BOT = [
  'red-blue_buff', 'red-gromp', 'red-wolves',
  'red-raptors', 'red-red_buff', 'red-krugs',
];
const BLUE_BOT_TO_TOP = [
  'red-red_buff', 'red-krugs', 'red-raptors',
  'red-wolves', 'red-blue_buff', 'red-gromp',
];
const RED_TOP_TO_BOT = [
  'blue-red_buff', 'blue-krugs', 'blue-raptors',
  'blue-wolves', 'blue-blue_buff', 'blue-gromp',
];
const RED_BOT_TO_TOP = [
  'blue-blue_buff', 'blue-gromp', 'blue-wolves',
  'blue-raptors', 'blue-red_buff', 'blue-krugs',
];

/** Get the full-clear route for a given side + direction */
export function getRoute(side: 'blue' | 'red', direction: RouteDirection): string[] {
  if (side === 'blue') return direction === 'top-to-bot' ? [...BLUE_TOP_TO_BOT] : [...BLUE_BOT_TO_TOP];
  return direction === 'top-to-bot' ? [...RED_TOP_TO_BOT] : [...RED_BOT_TO_TOP];
}

/** Fountain / base grid coordinates (blue = bottom-left, red = top-right) */
export function getBasePosition(side: 'blue' | 'red'): { gx: number; gy: number } {
  return side === 'blue' ? { gx: 25, gy: 490 } : { gx: 490, gy: 25 };
}

/* ── Gold / XP bridge ── */

/** Map RiftSimulator camp IDs to JungleSimulator CampIds for gold/XP lookup */
const CAMP_TYPE_MAP: Record<string, CampId> = {
  'blue_buff': 'blue',
  'red_buff': 'red',
  'gromp': 'gromp',
  'wolves': 'wolves',
  'raptors': 'raptors',
  'krugs': 'krugs',
  'scuttle': 'scuttle',
};

/** Extract the camp type from a RiftSimulator camp ID like "blue-blue_buff" → "blue_buff" */
function extractCampType(campId: string): string {
  const parts = campId.split('-');
  return parts.slice(1).join('-') || parts[0];
}

/** Get gold and XP for a camp at a given monster level */
export function getCampGoldXP(campId: string, monsterLevel: number = 1): { gold: number; xp: number } {
  const campType = extractCampType(campId);
  const campKey = CAMP_TYPE_MAP[campType];
  if (!campKey) return { gold: 0, xp: 0 };

  const campData = CAMPS[campKey];
  if (!campData) return { gold: 0, xp: 0 };

  const gold = campData.gold ?? (campData.goldRange ? Math.round(lerpByLevel(campData.goldRange, monsterLevel)) : 0);
  const xp = campData.xpRange ? Math.round(lerpByLevel(campData.xpRange, monsterLevel)) : 0;

  return { gold, xp };
}

/** Get champion level from cumulative XP */
export function getChampLevel(totalXP: number): number {
  return champLevelFromXP(totalXP).level;
}

/* ── Route helpers for subsequent clears ── */

/**
 * Filter a route to only include camps that will be spawned at or before `arrivalTime`.
 * Used for 2nd+ clears where buffs may not have respawned.
 */
export function getAvailableRoute(
  route: string[],
  camps: JungleCamp[],
  startGameTime: number,
  config: JungleModeConfig,
): string[] {
  const available: string[] = [];
  let currentTime = startGameTime;

  for (const campId of route) {
    const camp = camps.find(c => c.id === campId);
    if (!camp) continue;

    // Estimate walk time to this camp from previous position
    // (simplified: just check if camp will be alive by the time we could get there)
    const willBeAlive = camp.alive || (camp.nextSpawnAt <= currentTime + 30); // generous 30s window
    if (willBeAlive) {
      available.push(campId);
      currentTime += config.subsequentClearTime + 5; // rough estimate for walk + clear
    }
  }

  return available;
}

/**
 * Calculate the first camp in the route that will have respawned,
 * and the time we'd need to arrive there (after basing).
 */
export function getFirstRespawningCamp(
  route: string[],
  camps: JungleCamp[],
  currentGameTime: number,
  config: JungleModeConfig,
): { campId: string; walkTimeFromBase: number; campRespawnAt: number } | null {
  const base = getBasePosition(config.playerSide);

  for (const campId of route) {
    const camp = camps.find(c => c.id === campId);
    if (!camp) continue;

    // Skip camps that are currently alive (already available)
    // For next cycle, we want the first camp that will respawn
    const respawnAt = camp.alive ? currentGameTime : camp.nextSpawnAt;

    const dx = base.gx - camp.gx;
    const dy = base.gy - camp.gy;
    const distGrid = Math.sqrt(dx * dx + dy * dy);
    const walkTime = (distGrid * CELL_SIZE) / config.moveSpeed;

    return { campId, walkTimeFromBase: walkTime, campRespawnAt: respawnAt };
  }

  return null;
}

/**
 * Calculate free time after completing a clear cycle.
 * Free time = time until next camp respawns - (base time + walk time to that camp)
 */
export function calculateFreeTime(
  route: string[],
  camps: JungleCamp[],
  gameTimeAfterClear: number,
  config: JungleModeConfig,
): { freeTime: number; detail: string; nextCampId: string } {
  const base = getBasePosition(config.playerSide);

  // Find the earliest-respawning camp in the route that isn't currently alive
  let earliestCamp: { campId: string; respawnAt: number; walkFromBase: number } | null = null;

  for (const campId of route) {
    const camp = camps.find(c => c.id === campId);
    if (!camp) continue;

    // If the camp is dead, check when it respawns
    let respawnAt: number;
    if (camp.alive) {
      // Camp is still alive — it will be available immediately
      respawnAt = gameTimeAfterClear;
    } else {
      respawnAt = camp.nextSpawnAt;
    }

    const dx = base.gx - camp.gx;
    const dy = base.gy - camp.gy;
    const distGrid = Math.sqrt(dx * dx + dy * dy);
    const walkFromBase = (distGrid * CELL_SIZE) / config.moveSpeed;

    if (!earliestCamp || respawnAt < earliestCamp.respawnAt) {
      earliestCamp = { campId, respawnAt, walkFromBase };
    }
  }

  if (!earliestCamp) {
    return { freeTime: 0, detail: 'No camps available', nextCampId: route[0] };
  }

  const totalTravelTime = config.baseTime + earliestCamp.walkFromBase;
  const freeTime = Math.max(0, earliestCamp.respawnAt - gameTimeAfterClear - totalTravelTime);

  const campName = camps.find(c => c.id === earliestCamp!.campId)?.name ?? earliestCamp.campId;
  const detail = `Base ${config.baseTime}s + walk ${Math.round(earliestCamp.walkFromBase)}s to ${campName}`;

  return { freeTime: Math.round(freeTime), detail, nextCampId: earliestCamp.campId };
}

/**
 * Compute straight-line walk time between two grid positions.
 */
export function straightLineWalkTime(
  fromGx: number, fromGy: number,
  toGx: number, toGy: number,
  moveSpeed: number,
): number {
  const dx = toGx - fromGx;
  const dy = toGy - fromGy;
  const distGrid = Math.sqrt(dx * dx + dy * dy);
  return (distGrid * CELL_SIZE) / moveSpeed;
}

/* ── Scuttle camp IDs for each route direction ── */

/** Get the scuttle crab camp ID closest to the end of a route */
export function getScuttleForRoute(side: 'blue' | 'red', direction: RouteDirection): string {
  // After clearing, the jungler is at the "bot" end of their route.
  // Top-to-bot ends near bot → scuttle-bot is closer
  // Bot-to-top ends near top → scuttle-top is closer
  if (direction === 'top-to-bot') return 'scuttle-bot';
  return 'scuttle-top';
}

/**
 * Compute the average clear time per camp for the first clear.
 *
 * Given the user's desired clear finish time and the route, we subtract all
 * walk times (start offset → camp1, camp1 → camp2, … camp5 → camp6) from
 * the total elapsed time to get total clearing time, then divide by 6.
 *
 * If scuttleTime > 0, we also compute the walk time from last camp to scuttle
 * and ensure the total sums fit.
 *
 * Returns { avgClearTime, totalWalkTime, breakdown } where breakdown has
 * per-segment walk times for display.
 */
export function computeFirstClearTiming(
  route: string[],
  camps: JungleCamp[],
  config: JungleModeConfig,
): {
  avgClearTime: number;
  totalWalkTime: number;
  walkToScuttle: number;
  scuttleCampId: string;
  breakdown: Array<{ from: string; to: string; walkTime: number }>;
} {
  const campPositions = route.map(id => {
    const c = camps.find(camp => camp.id === id);
    return c ? { id, gx: c.gx, gy: c.gy } : null;
  }).filter(Boolean) as Array<{ id: string; gx: number; gy: number }>;

  const breakdown: Array<{ from: string; to: string; walkTime: number }> = [];
  let totalWalkTime = 0;

  // Walk from spawn offset to first camp (small, ~1-2s)
  // For simplicity, count this as 0 since the jungler starts right next to the camp

  // Walk between consecutive camps
  // A* paths are ~1.3x longer than straight-line due to terrain/walls
  const PATH_CORRECTION = 1.3;
  for (let i = 0; i < campPositions.length - 1; i++) {
    const from = campPositions[i];
    const to = campPositions[i + 1];
    const wt = straightLineWalkTime(from.gx, from.gy, to.gx, to.gy, config.moveSpeed) * PATH_CORRECTION;
    totalWalkTime += wt;
    breakdown.push({ from: from.id, to: to.id, walkTime: wt });
  }

  // Walk from last camp to scuttle
  let walkToScuttle = 0;
  const scuttleCampId = getScuttleForRoute(config.playerSide, config.routeDirection);
  if (config.scuttleTime > 0 && campPositions.length > 0) {
    const lastCamp = campPositions[campPositions.length - 1];
    const scuttleCamp = camps.find(c => c.id === scuttleCampId);
    if (scuttleCamp) {
      walkToScuttle = straightLineWalkTime(
        lastCamp.gx, lastCamp.gy, scuttleCamp.gx, scuttleCamp.gy, config.moveSpeed,
      );
    }
  }

  // Total elapsed = clearFinishTime - startTime
  const totalElapsed = config.clearFinishTime - config.startTime;
  const numCamps = campPositions.length;

  // avgClearTime = (totalElapsed - totalWalkTime) / numCamps
  const avgClearTime = numCamps > 0
    ? Math.max(1, (totalElapsed - totalWalkTime) / numCamps)
    : 10;

  return { avgClearTime, totalWalkTime, walkToScuttle, scuttleCampId, breakdown };
}
